import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Heart, ArrowUpRight, Loader2, ShieldCheck, Check } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader, Kicker } from "../components/Bits";
import { Reveal } from "../components/Reveal";
import { api } from "../lib/api";
import { EVENT } from "../lib/site";

const PRESETS = [25, 50, 100, 250];
const PROGRAMS = [
  "Children's Weekend Respite for youth ages 6-18",
  "Supported living & life-skills programs",
  "Skills to Success (S2S) employment support",
  "Social enterprises like the Well Preserved jam co.",
  "Family supports & community participation",
];

export default function SponsorDonation() {
  const [params] = useSearchParams();
  const preParam = parseFloat(params.get("amount"));
  const hasPreset = PRESETS.includes(preParam);
  const [amount, setAmount] = useState(hasPreset ? preParam : 50);
  const [custom, setCustom] = useState(Number.isFinite(preParam) && preParam > 0 && !hasPreset ? String(preParam) : "");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const chosen = custom !== "" ? parseFloat(custom) : amount;

  const donate = async (e) => {
    e.preventDefault();
    const value = custom !== "" ? parseFloat(custom) : amount;
    if (!value || value < 1) {
      toast.error("Please enter a donation amount of at least $1.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        amount: value,
        origin_url: window.location.origin,
        name,
        email,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  const field = "w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-ink placeholder:text-ink-soft/60 outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 transition-colors duration-200";

  return (
    <>
      <Seo title="Sponsor Donation" path="/sponsor-donation" description={`Make a secure one-time donation to ${EVENT.charity} through the Grimsby On The Lake Turkey Trot.`} />
      <PageHeader
        chapter="give back"
        title="Sponsor a donation."
        sub={`Make a secure one-time gift in support of ${EVENT.charity}. Every dollar helps build a more inclusive West Niagara.`}
      />

      <section className="mx-auto max-w-5xl px-5 sm:px-8 pb-16 grid gap-8 lg:grid-cols-5 items-start">
        <Reveal className="lg:col-span-3">
          <form onSubmit={donate} className="rounded-3xl bg-white border border-black/5 shadow-warm p-8 sm:p-10" data-testid="donation-form">
            <Kicker>choose an amount (CAD)</Kicker>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {PRESETS.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => { setAmount(a); setCustom(""); }}
                  className={`py-4 rounded-2xl font-display text-2xl font-semibold transition-all duration-200 border ${
                    custom === "" && amount === a
                      ? "bg-terracotta text-white border-terracotta scale-105 shadow-warm"
                      : "bg-cream-200 text-ink border-transparent hover:border-terracotta/40"
                  }`}
                  data-testid={`donation-preset-${a}`}
                >
                  ${a}
                </button>
              ))}
            </div>

            <div className="mt-5">
              <label className="text-sm font-medium text-ink-soft">Or enter a custom amount</label>
              <div className="mt-2 relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 font-display text-xl text-ink-soft">$</span>
                <input
                  type="number" min="1" step="1" placeholder="Custom amount"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  className={`${field} pl-9`}
                  data-testid="donation-custom"
                />
              </div>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <input placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} className={field} data-testid="donation-name" />
              <input type="email" placeholder="Email (optional)" value={email} onChange={(e) => setEmail(e.target.value)} className={field} data-testid="donation-email" />
            </div>

            <button type="submit" disabled={loading} className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-terracotta text-white font-semibold hover:bg-sienna transition-colors duration-200 disabled:opacity-60" data-testid="donation-submit">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Redirecting to secure checkout…</>
                : <><Heart size={16} /> Donate {chosen && chosen >= 1 ? `$${Number(chosen).toFixed(0)}` : ""} <ArrowUpRight size={16} /></>}
            </button>

            <p className="mt-4 flex items-center gap-2 text-xs text-ink-soft">
              <ShieldCheck size={14} className="text-forest" /> Secure payment powered by Stripe. Cards accepted.
            </p>
          </form>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-2">
          <div className="rounded-3xl bg-forest text-cream p-8" data-testid="impact-card">
            <p className="font-accent text-2xl text-ochre">your impact</p>
            <h3 className="mt-1 font-display text-2xl font-semibold">Empowering every stage of life.</h3>
            <p className="mt-3 text-sm text-cream/80 leading-relaxed">
              Inclusion West Niagara supports people with intellectual and developmental disabilities across
              West Niagara — "Supporting Dreams, Building Community, Celebrating Abilities." Your gift helps fund:
            </p>
            <ul className="mt-4 space-y-2">
              {PROGRAMS.map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <Check size={16} className="mt-0.5 shrink-0 text-ochre" />
                  <span className="text-sm text-cream/85">{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 flex gap-6 border-t border-cream/15 pt-4">
              <div>
                <p className="font-display text-3xl font-semibold text-ochre leading-none">360+</p>
                <p className="mt-1 text-xs text-cream/70">people supported</p>
              </div>
              <div>
                <p className="font-display text-3xl font-semibold text-ochre leading-none">200</p>
                <p className="mt-1 text-xs text-cream/70">staff & caregivers</p>
              </div>
            </div>
            <a
              href="https://inclusionwestniagara.ca/"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 text-ochre font-semibold text-sm hover:text-cream transition-colors duration-200"
              data-testid="impact-learn-more"
            >
              Learn more at inclusionwestniagara.ca <ArrowUpRight size={15} />
            </a>
          </div>
        </Reveal>
      </section>
    </>
  );
}
